import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    PenTool,
    Users,
    Sparkles,
    Globe,
    ShieldCheck,
    Heart,
    ChevronRight,
    ArrowDown
} from "lucide-react";
import heroImage from "../assets/about-hero.png";

const AboutPage = () => {
    const [isLoaded, setIsLoaded] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        setIsLoaded(true);

        const observerOptions = {
            threshold: 0.1,
            rootMargin: "0px 0px -50px 0px"
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('active');
                    // Once animated, no need to observe anymore
                    observer.unobserve(entry.target);
                }
            });
        }, observerOptions);

        const animatedElements = document.querySelectorAll('.scroll-reveal');
        animatedElements.forEach(el => observer.observe(el));

        return () => observer.disconnect();
    }, []);

    return (
        <div className="min-h-screen bg-white text-gray-900 font-sans overflow-x-hidden relative flex flex-col">
            {/* Logo in top right corner */}
            <div className="absolute top-6 right-8 z-50">
                <button
                    onClick={() => navigate('/')}
                    className="cursor-pointer focus:outline-none"
                    aria-label="Back to home"
                >
                    <img
                        src="/logos/Wryven-Feather-Color.png"
                        alt="Wryven Logo"
                        className="w-32 h-32 md:w-48 md:h-48 object-contain hover:scale-105 transition-transform duration-300 drop-shadow-lg"
                    />
                </button>
            </div>

            {/* Hero Section */}
            <section className="relative min-h-screen flex-shrink-0 flex items-center justify-center overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-purple-50 animate-gradient-slow border-b border-gray-50">
                <div className="container mx-auto px-6 z-10 text-center">
                    <div className={`transition-all duration-1000 transform ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                        <h1 className="wryven-font text-8xl md:text-[12rem] text-indigo-600 mb-6 drop-shadow-sm">
                            Wryven
                        </h1>
                    </div>
                    <p className={`text-xl md:text-3xl font-light text-gray-600 mb-10 max-w-2xl mx-auto transition-all duration-1000 delay-300 transform ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} heading-outfit tracking-tight`}>
                        The sanctuary where every word finds its wings and every story discovers its voice.
                    </p>
                    <div className={`flex flex-col sm:flex-row gap-4 justify-center transition-all duration-1000 delay-500 transform ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                        <button
                            onClick={() => navigate('/signup')}
                            className="bg-indigo-600 text-white px-10 py-4 rounded-full text-lg font-semibold hover:bg-indigo-700 transition-all transform hover:scale-105 shadow-xl hover:shadow-indigo-200"
                        >
                            Get Started
                        </button>
                        <button
                            onClick={() => navigate('/login')}
                            className="bg-white/80 backdrop-blur-sm border-2 border-indigo-100 text-indigo-600 px-10 py-4 rounded-full text-lg font-semibold hover:bg-indigo-50 transition-all shadow-md"
                        >
                            Login
                        </button>
                    </div>
                </div>

                {/* Scroll Indicator */}
                <div className={`absolute bottom-10 left-1/2 -translate-x-1/2 transition-all duration-1000 delay-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="flex flex-col items-center gap-2 text-gray-400">
                        <span className="text-xs uppercase tracking-widest font-medium">Explore</span>
                        <ArrowDown size={20} className="animate-bounce" />
                    </div>
                </div>

                {/* Animated Background Shapes */}
                <div className="absolute top-1/4 -left-20 w-80 h-80 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" />
                <div className="absolute bottom-1/4 -right-20 w-[30rem] h-[30rem] bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" style={{ animationDelay: '2s' }} />
            </section>

            {/* Main Content Area */}
            <div className="flex-1">
                {/* Our Story Section */}
                <section className="py-32 bg-white overflow-hidden">
                    <div className="container mx-auto px-6">
                        <div className="flex flex-col md:flex-row items-center gap-20">
                            <div className="flex-1 space-y-8 scroll-reveal scroll-reveal-left">
                                <h2 className="text-5xl font-bold tracking-tight text-gray-900 leading-tight heading-playfair">Crafting a home for the unwritten.</h2>
                                <p className="text-xl text-gray-600 leading-relaxed font-light">
                                    Wryven was born out of a simple realization: the world is full of incredible writings that never get told. We wanted to build a home for the Shayars, the poets, the storytellers, and the writers who need more than just a blank page—they need a community.
                                </p>
                                <p className="text-xl text-gray-600 leading-relaxed font-light">
                                    From a small project for writers to a global platform for creative expression, Wryven has always prioritized the creator's voice. We believe in the power of words to change minds, bridge cultures, and inspire the next generation.
                                </p>
                            </div>
                            <div className="flex-1 relative scroll-reveal scroll-reveal-right">
                                <div className="relative z-10 rounded-2xl overflow-hidden shadow-[0_32px_64px_-16px_rgba(0,0,0,0.1)] transform hover:scale-[1.02] transition-transform duration-700">
                                    <img
                                        src={heroImage}
                                        alt="Creative Workspace"
                                        className="w-full h-auto object-cover"
                                    />
                                </div>
                                <div className="absolute -inset-10 bg-indigo-50 rounded-full blur-3xl -z-10 animate-float" />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Mission & Values */}
                <section className="py-32 bg-gray-50/50">
                    <div className="container mx-auto px-6 text-center">
                        <h2 className="text-5xl font-bold mb-20 scroll-reveal scroll-reveal-up heading-playfair">The <span className="text-7xl font-bold mb-20 ml-2 scroll-reveal scroll-reveal-up wryven-font">Wryven</span> Way</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {/* Value 1 */}
                            <div className="p-10 bg-white rounded-3xl shadow-sm hover:shadow-2xl transition-all duration-500 group scroll-reveal scroll-reveal-up" style={{ transitionDelay: '0.1s' }}>
                                <div className="w-20 h-20 bg-indigo-50 rounded-3xl flex items-center justify-center mb-8 mx-auto group-hover:bg-indigo-600 group-hover:text-white group-hover:rotate-6 transition-all duration-500">
                                    <PenTool size={40} />
                                </div>
                                <h3 className="text-2xl font-semibold mb-4 text-indigo-600 heading-outfit">Pure Expression</h3>
                                <p className="text-gray-500 text-lg leading-relaxed">Your voice, unfiltered. We provide the tools for Shayari, Novels, Scripts, and beyond.</p>
                            </div>

                            {/* Value 2 */}
                            <div className="p-10 bg-white rounded-3xl shadow-sm hover:shadow-2xl transition-all duration-500 group scroll-reveal scroll-reveal-up" style={{ transitionDelay: '0.2s' }}>
                                <div className="w-20 h-20 bg-purple-50 rounded-3xl flex items-center justify-center mb-8 mx-auto group-hover:bg-purple-600 group-hover:text-white group-hover:-rotate-6 transition-all duration-500">
                                    <Users size={40} />
                                </div>
                                <h3 className="text-2xl font-semibold mb-4 text-purple-600 heading-outfit">Kind Community</h3>
                                <p className="text-gray-500 text-lg leading-relaxed">Connect with a global tribe of creators who value constructive growth and creative collaboration.</p>
                            </div>

                            {/* Value 3 */}
                            <div className="p-10 bg-white rounded-3xl shadow-sm hover:shadow-2xl transition-all duration-500 group scroll-reveal scroll-reveal-up" style={{ transitionDelay: '0.3s' }}>
                                <div className="w-20 h-20 bg-pink-50 rounded-3xl flex items-center justify-center mb-8 mx-auto group-hover:bg-pink-600 group-hover:text-white group-hover:rotate-12 transition-all duration-500">
                                    <Sparkles size={40} />
                                </div>
                                <h3 className="text-2xl font-semibold mb-4 text-pink-600 heading-outfit">Endless Inspiration</h3>
                                <p className="text-gray-500 text-lg leading-relaxed">Discover new genres and perspectives every day through our feeds.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Commitment to Safety */}
                <section className="py-32 bg-white">
                    <div className="container mx-auto px-6 max-w-5xl">
                        <div className="bg-indigo-50/50 p-16 rounded-[3rem] flex flex-col md:flex-row items-center gap-12 scroll-reveal scroll-reveal-zoom">
                            <ShieldCheck size={100} className="text-indigo-600 shrink-0" />
                            <div>
                                <h2 className="text-4xl font-bold mb-6 heading-outfit">Your Sanctuary, Protected.</h2>
                                <p className="text-gray-600 text-xl mb-8 leading-relaxed font-light">
                                    Creativity thrives in safe spaces. That's why we've built industry-leading moderation tools and privacy controls to ensure Wryven remains a positive environment for everyone.
                                </p>
                                <a href="/policy" className="text-indigo-600 text-lg font-semibold flex items-center gap-2 hover:gap-4 transition-all group heading-outfit">
                                    Learn more about our Community Guidelines <ChevronRight size={24} className="group-hover:translate-x-1 transition-transform" />
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Footer */}
                <section className="py-32 text-center bg-gray-50/50">
                    <div className="container mx-auto px-6 scroll-reveal scroll-reveal-up">
                        <Heart size={64} className="text-pink-500 mx-auto mb-8 animate-pulse" />
                        <h2 className="text-5xl font-bold mb-8 heading-playfair italic">Ready to share your story?</h2>
                        <p className="text-gray-500 text-2xl mb-12 max-w-2xl mx-auto font-light leading-relaxed heading-outfit">
                            Join fellow creators and begin your journey on Wryven today.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                            <button
                                onClick={() => navigate('/signup')}
                                className="bg-indigo-600 text-white px-12 py-5 rounded-full text-xl font-semibold hover:bg-indigo-700 transition-all shadow-xl hover:shadow-indigo-200 transform hover:scale-105 heading-outfit"
                            >
                                Join Wryven
                            </button>
                            <button
                                onClick={() => navigate('/explore')}
                                className="text-indigo-600 text-xl font-semibold hover:underline bg-transparent px-8 py-4 heading-outfit font-medium"
                            >
                                Explore the Feed
                            </button>
                        </div>
                    </div>
                </section>
            </div>

            {/* Compact Footer Section */}
            <footer className="bg-white py-8 px-6 border-t border-gray-100 flex-shrink-0">
                <div className="container mx-auto">
                    <div className="flex justify-center items-center flex-wrap gap-x-6 gap-y-3 mb-4">
                        {[
                            { label: "Terms", path: "/policy?tab=terms" },
                            { label: "Privacy", path: "/policy?tab=privacy" },
                            { label: "Cookies", path: "/policy?tab=cookies" },
                        ].map((item) => (
                            <a
                                key={item.label}
                                href={item.path}
                                onClick={(e) => {
                                    if (item.path !== "#") {
                                        e.preventDefault();
                                        navigate(item.path);
                                    }
                                }}
                                className="text-sm text-gray-400 hover:text-indigo-600 transition-all font-light heading-outfit"
                            >
                                {item.label}
                            </a>
                        ))}
                    </div>
                    <div className="text-center">
                        <p className="text-xs text-gray-400 tracking-widest uppercase heading-outfit">
                            © {new Date().getFullYear()} Wryven. Created for writers.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default AboutPage;
